import { useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Menu, X } from 'lucide-react';
import { ThemeToggle } from '../../shell/ThemeToggle';
import { useBrand } from '../../brand/BrandProvider';

/**
 * Shared marketing chrome for the public product pages. Brand-aware (Decision
 * #21): on the platform host it's Possignin's own brand; on a reseller apex the
 * wordmark, nav, and footer wear the RESELLER's brand and drop every
 * platform-only link (Features/Resellers/About/Blog point at Possignin-flavoured
 * content, so they're hidden rather than leaked). Follows the branding seven
 * rules: one accent (`brand`), restraint, Inter, light + dark, mobile-first,
 * tabular numerals.
 */

// Platform nav. On a reseller host we show only the brand-safe surface (Pricing),
// since Features/About/Blog render platform marketing copy and "Resellers" is a
// platform-only funnel — none belong on a reseller's white-label domain.
const NAV = [
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Resellers', to: '/become-a-reseller' },
  { label: 'About', to: '/about' },
  { label: 'Blog', to: '/blog' },
] as const;

const RESELLER_NAV = [{ label: 'Pricing', to: '/pricing' }] as const;

/**
 * Wordmark — brand-aware. Platform: the geometric "P" glyph + "Possignin", linked
 * to the product home. Reseller: the reseller's uploaded logo (if any) or an
 * accent square with the brand initial + the brand name, linked to the host home
 * ("/" → the reseller landing). The accent already follows the brand via
 * `--color-brand`, so no per-brand colour is needed here.
 */
export function Wordmark({ className = '' }: { className?: string }) {
  const brand = useBrand();

  if (brand.isPlatform) {
    return (
      <Link
        to="/possignin"
        className={`group inline-flex items-center gap-2.5 rounded-lg ${className}`}
        aria-label="Possignin home"
      >
        <span
          className="w-8 h-8 rounded-lg bg-brand grid place-items-center text-white shadow-sm transition-transform duration-200 group-hover:scale-[1.04] dark:bg-brand-dm dark:text-zinc-950"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 4h9a5 5 0 0 1 0 10H8v6" />
          </svg>
        </span>
        <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">
          Possignin
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/"
      className={`group inline-flex items-center gap-2.5 rounded-lg ${className}`}
      aria-label={`${brand.name} home`}
    >
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt={brand.name} className="h-8 w-auto max-w-[180px] object-contain" />
      ) : (
        <>
          <span
            className="w-8 h-8 rounded-lg bg-brand grid place-items-center text-white font-semibold text-[15px] shadow-sm transition-transform duration-200 group-hover:scale-[1.04] dark:bg-brand-dm dark:text-zinc-950"
            aria-hidden="true"
          >
            {(brand.shortName || brand.name).charAt(0).toUpperCase()}
          </span>
          <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </>
      )}
    </Link>
  );
}

export function MarketingShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const brand = useBrand();
  const nav = brand.isPlatform ? NAV : RESELLER_NAV;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark text-zinc-900 dark:text-zinc-100">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-brand focus:text-white focus:text-sm focus:font-medium focus:shadow-lg dark:focus:bg-brand-dm dark:focus:text-zinc-950"
      >
        Skip to content
      </a>

      <header
        className={
          'sticky top-0 z-40 border-b transition-colors duration-200 ' +
          (scrolled
            ? 'border-zinc-200/80 dark:border-zinc-800/80 bg-white/85 dark:bg-zinc-950/85 backdrop-blur-md supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-zinc-950/70'
            : 'border-transparent bg-transparent')
        }
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Wordmark />
          <nav className="hidden md:flex items-center gap-7 text-sm" aria-label="Primary">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2.5">
            <Link
              to="/login"
              className="text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50 px-2 py-1 rounded-md transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand-hover shadow-xs transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
            >
              Start free
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <ThemeToggle />
          </div>
          <button
            type="button"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden w-11 h-11 -mr-2 grid place-items-center rounded-lg text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md px-4 py-3 space-y-1">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                {n.label}
              </Link>
            ))}
            <div className="flex items-center justify-between px-1 pt-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Theme</span>
              <ThemeToggle />
            </div>
            <div className="pt-2 flex items-center gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                onClick={() => setOpen(false)}
                className="flex-1 text-center px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-brand dark:bg-brand-dm dark:text-zinc-950"
              >
                Start free
              </Link>
            </div>
          </div>
        )}
      </header>

      <main id="main">{children}</main>

      <MarketingFooter />
    </div>
  );
}

const LEGAL_LINKS = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Refund policy', to: '/refund' },
  { label: 'Cookies', to: '/cookies' },
];

function MarketingFooter() {
  const brand = useBrand();
  const isReseller = !brand.isPlatform;

  // Reseller footer: only the brand-safe Product + Legal columns (no ITCity /
  // Possignin / "become a reseller" links). Platform: the full chrome.
  const cols: { title: string; links: { label: string; to: string }[] }[] = isReseller
    ? [
        {
          title: 'Product',
          links: [
            { label: 'Pricing', to: '/pricing' },
            { label: 'Sign in', to: '/login' },
            { label: 'Start free', to: '/signup' },
          ],
        },
        { title: 'Legal', links: LEGAL_LINKS },
      ]
    : [
        {
          title: 'Product',
          links: [
            { label: 'Features', to: '/features' },
            { label: 'Pricing', to: '/pricing' },
            { label: 'Become a reseller', to: '/become-a-reseller' },
            { label: 'Sign in', to: '/login' },
            { label: 'Start free', to: '/signup' },
          ],
        },
        {
          title: 'Company',
          links: [
            { label: 'About ITCity', to: '/home' },
            { label: 'About Possignin', to: '/about' },
            { label: 'Blog', to: '/blog' },
          ],
        },
        { title: 'Legal', links: LEGAL_LINKS },
      ];

  return (
    <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-2 sm:grid-cols-4 gap-8 lg:gap-10">
        <div className="col-span-2 sm:col-span-1">
          <Wordmark />
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-[17rem]">
            {isReseller
              ? 'Cloud POS & business software. Priced in taka, fluent in Bangla.'
              : 'Cloud POS & business software, built in Sylhet, Bangladesh — by ITCity. Priced in taka, fluent in Bangla.'}
          </p>
          <a
            href={`mailto:${brand.supportEmail}`}
            className="mt-4 inline-block text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
          >
            {brand.supportEmail}
          </a>
        </div>
        {cols.map((c) => (
          <nav key={c.title} aria-label={c.title}>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              {c.title}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {c.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <div className="border-t border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400">
          <p className="tabular-nums">© {YEAR} {isReseller ? brand.name : 'ITCity'}. All rights reserved.</p>
          <p className="inline-flex items-center gap-1.5">
            <span className="relative flex w-1.5 h-1.5" aria-hidden="true">
              <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </span>
            All systems operational
          </p>
        </div>
      </div>
    </footer>
  );
}

// Fixed to keep the build deterministic in tests.
const YEAR = 2026;
